import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Tag, Calendar, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PromoPopup = () => {
  const [promotion, setPromotion] = useState(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem('promoPopupDismissed');
    if (!dismissed) {
      fetchPromotion();
    }
  }, []);

  const fetchPromotion = async () => {
    try {
      const response = await axios.get(`${API}/promotion`);
      const promo = response.data.promotion;
      if (promo && promo.isActive) {
        if (promo.validUntil) {
          const validUntil = new Date(promo.validUntil);
          if (validUntil <= new Date()) return;
        }
        setPromotion(promo);
        setTimeout(() => setVisible(true), 1000);
      }
    } catch (error) {
      console.error('Error fetching promotion:', error);
    }
  };

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => handleClose(), 5000);
    return () => clearTimeout(timer);
  }, [visible]);

  const handleClose = () => {
    setClosing(true);
    sessionStorage.setItem('promoPopupDismissed', 'true');
    setTimeout(() => setVisible(false), 300);
  };

  if (!visible || !promotion) return null;

  const badgeText = promotion.type === 'gift'
    ? (promotion.customDisplayText || 'Poklon!')
    : promotion.applyDiscount
      ? `${promotion.discountPercent}% OFF`
      : (promotion.customDisplayText || 'Specijalna Ponuda');

  return (
    <div
      data-testid="promo-popup-overlay"
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-opacity duration-300 ${closing ? 'opacity-0' : 'opacity-100'}`}
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={handleClose}
    >
      <div
        data-testid="promo-popup-card"
        className={`relative w-11/12 max-w-md transform transition-all duration-300 ${closing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-br from-orange-600 via-red-600 to-orange-600 text-white rounded-2xl shadow-2xl overflow-hidden relative">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }} />
          </div>
          <button
            data-testid="promo-popup-close-btn"
            onClick={handleClose}
            className="absolute top-2 right-2 md:top-3 md:right-3 z-10 bg-white/30 hover:bg-white/50 text-white rounded-full p-2 md:p-1.5 transition-all min-w-[40px] min-h-[40px] flex items-center justify-center"
            aria-label="Zatvori"
          >
            <X size={20} />
          </button>
          <div className="relative px-5 py-5 md:p-8 text-center">
            <div className="inline-flex items-center gap-1.5 md:gap-2 bg-white text-orange-600 px-4 py-2 md:px-6 md:py-3 rounded-full font-bold text-lg md:text-3xl shadow-lg mb-4 md:mb-5">
              <Sparkles size={18} className="md:w-6 md:h-6" />
              <span>{badgeText}</span>
            </div>
            <h2 className="text-base md:text-2xl font-bold mb-3 md:mb-4 leading-tight">
              {promotion.message}
            </h2>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-xs md:text-sm opacity-90 mb-4 md:mb-5">
              <span className="flex items-center gap-1">
                <Tag size={13} />
                {promotion.format === 'all' ? 'Svi formati' : `${promotion.format} cm`}
              </span>
              {promotion.validUntil && (
                <span className="flex items-center gap-1">
                  <Calendar size={13} />
                  Do: {new Date(promotion.validUntil).toLocaleDateString('sr-RS', {
                    day: 'numeric', month: 'numeric', year: 'numeric'
                  })}
                </span>
              )}
            </div>
            <a
              href="/upload"
              onClick={handleClose}
              data-testid="promo-popup-cta"
              className="inline-block bg-white text-orange-600 px-6 py-2.5 md:px-8 md:py-3 rounded-full font-bold text-base md:text-lg hover:bg-orange-50 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Iskoristi Sada!
            </a>
          </div>
          <div className="h-1 bg-white/20">
            <div
              className="h-full bg-white rounded-full"
              style={{ animation: 'popupTimer 5s linear forwards' }}
            />
          </div>
        </div>
      </div>
      <style>{`
        @keyframes popupTimer {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
};

export default PromoPopup;
